/*
 * MAW music: procedural lounge jazz + the decay chain, act layers and bonus loops (docs/07 §4.1).
 *
 * STRUCTURE (tweak here)
 * ─ Tempo/feel: BPM = 92, swung eighths (SWING = 0.64 of a beat for the on-beat eighth).
 * ─ Form: 32 bars AB, one chord per bar (PROG), D minor-ish ii–V–I cycles:
 *     A: Dm9 G13 Cmaj9 A7b9 | Dm9 G13 Em7b5 A7b9 | Gm9 C9 Fmaj9 Bbmaj9 | Em7b5 A7b9 Dm9 A7b9
 *     B: Bbmaj9 Eb9 Bbmaj9 F13 | Gm9 C9 Fmaj9 D7b9 | Gm9 C13 Am7b5 D7b9 | Gm9 A7b9 Dm9 A7b9
 * ─ Act layers (setAct): II a sour vibraphone, III an organ pad, IV organ + choir.
 * ─ Bonus loops (setMode): ragtime scratch, tick-tock Hi-Lo, carnival waltz, pachinko jingle,
 *   big-band Feast, over a 4-bar turnaround; the band and act layers step aside meanwhile.
 * ─ Scheduler: setInterval(25 ms) looks 150 ms ahead on ctx.currentTime (1.2 s when the tab
 *   is hidden, since timers are throttled) and schedules 8th-note steps (128 per loop).
 *   If it falls behind, it realigns and skips the missed notes rather than bunching them.
 * ─ Instruments (each its own GainNode → preMix):
 *     bass   walking quarters: root, chord tone, chord tone, chromatic approach to next root.
 *            triangle+sine, plucky envelope, closing lowpass. Range A1..~A3.
 *     comp   Rhodes-ish rootless 4-note voicings (sine + detuned triangle + short 4f "tine"),
 *            Charleston-type rhythms picked per bar, bus tremolo 4.8 Hz.
 *     drums  brushed snare swish on 2 & 4 (+ ghost swishes), swung ride "ding ding-a",
 *            feathered kick. Dropped as decay goes 0.7 → 0.8.
 *     piano  detuned honky piano (decay layer), fades in for decay 0.6 → 0.9.
 *     drone  low D saws through a resonant lowpass, fades in for decay 0.7 → 1.
 * ─ Decay chain (setDecay d ∈ 0..1, all changes smoothed with setTargetAtTime):
 *     preMix → tape (modulated DelayNode: wow 0.55 Hz up to ±3% pitch, flutter 6.5 Hz ±0.3%)
 *            → [clean | WaveShaper tanh+quantize ("saturation/bitcrush"), wet 0 → 0.3]
 *            → lowpass 18 kHz → 2.5 kHz (exponential in d)
 *            → [dry | Convolver (generated 2.8 s noise impulse), wet 10% → 45%]
 *            → duck gain → out gain (start/stop fades) → music bus (audio.ts).
 * ─ tapeStop(): the tape delay line's delayTime follows D0 + t²/2T, i.e. the playback rate
 *   ramps linearly 1 → 0 over TAPE_STOP_S, pitching everything (incl. held notes) down.
 *
 * Levels: instrument peaks sum to roughly 0.6 before the music bus (which audio.ts keeps at
 * ≤ 0.35), so there's headroom for the reverb and SFX.
 */

import { noiseBuffer } from './sfx';

export interface MusicEngine {
  attach(ctx: AudioContext, dest: AudioNode): void;
  start(): void;
  stop(): void;
  setDecay(d: number): void;
  duck(db: number, ms: number): void;
  tapeStop(): void;
  /** Act layers (docs/07 §4.1): 0 dry, 1 + sour vibraphone, 2 + organ pad, 3 + organ and choir. */
  setAct(act: number): void;
  /** A bonus game's own short loop replaces the band while it runs (null = back to the band). */
  setMode(mode: MusicMode | null): void;
}

export type MusicMode = 'scratch' | 'hilo' | 'wheel' | 'plinko' | 'feast';

// ── Tunables ─────────────────────────────────────────────────────────────────────
const BPM = 92;
const BEAT = 60 / BPM;
const SWING = 0.64;
const BARS = 32;
const STEPS_PER_BAR = 8;
const LOOP_STEPS = BARS * STEPS_PER_BAR;
const LOOKAHEAD = 0.15;
const LOOKAHEAD_HIDDEN = 1.2;
const TICK_MS = 25;

const LEVEL = { bass: 0.15 /* ×2 oscillators */, comp: 0.05 /* per voice */, snare: 0.07, ride: 0.028, kick: 0.05, piano: 0.045, drone: 0.1, vibes: 0.035, organ: 0.018, choir: 0.014, bell: 0.03, brass: 0.03 };

const TAPE_D0 = 0.02; // base delay of the tape line (s)
const WOW_HZ = 0.55;
const FLUTTER_HZ = 6.5;
const WOW_MAX = 0.03 / (2 * Math.PI * WOW_HZ); // delay amplitude giving ±3% pitch
const FLUTTER_MAX = 0.003 / (2 * Math.PI * FLUTTER_HZ); // ±0.3%
const TAPE_STOP_S = 1.6;
const SMOOTH = 0.4; // setTargetAtTime time constant for decay changes

// ── Harmony ──────────────────────────────────────────────────────────────────────
type Quality = 'm9' | '13' | 'maj9' | '7b9' | 'm7b5' | '9';
/** Rootless comp voicing intervals, and bass chord tones (above root). */
const QUAL: Record<Quality, { voicing: number[]; bass: number[] }> = {
  m9: { voicing: [3, 7, 10, 14], bass: [0, 3, 7, 10] },
  '13': { voicing: [4, 10, 14, 21], bass: [0, 4, 7, 10] },
  maj9: { voicing: [4, 7, 11, 14], bass: [0, 4, 7, 11] },
  '7b9': { voicing: [4, 10, 13, 20], bass: [0, 4, 7, 10] },
  m7b5: { voicing: [3, 6, 10, 12], bass: [0, 3, 6, 10] },
  '9': { voicing: [4, 10, 14, 19], bass: [0, 4, 7, 10] },
};
// Pitch classes: C0 D2 E4 F5 G7 A9 Bb10
const PROG: readonly [number, Quality][] = [
  // A
  [2, 'm9'], [7, '13'], [0, 'maj9'], [9, '7b9'],
  [2, 'm9'], [7, '13'], [4, 'm7b5'], [9, '7b9'],
  [7, 'm9'], [0, '9'], [5, 'maj9'], [10, 'maj9'],
  [4, 'm7b5'], [9, '7b9'], [2, 'm9'], [9, '7b9'],
  // B (bridge): up to Bb, a turn through Eb and back down to D minor
  [10, 'maj9'], [3, '9'], [10, 'maj9'], [5, '13'],
  [7, 'm9'], [0, '9'], [5, 'maj9'], [2, '7b9'],
  [7, 'm9'], [0, '13'], [9, 'm7b5'], [2, '7b9'],
  [7, 'm9'], [9, '7b9'], [2, 'm9'], [9, '7b9'],
];
/** The bonus loops cycle a short ii–V–I–VI turnaround. */
const BONUS_PROG: readonly [number, Quality][] = [[2, 'm9'], [7, '13'], [0, 'maj9'], [9, '7b9']];

/** Comp rhythms: [step within bar (0..7), length in beats]. Steps: even = on-beat, odd = swung "and". */
const COMP_PATTERNS: readonly (readonly [number, number][])[] = [
  [[0, 1.0], [3, 1.4]], // Charleston
  [[1, 0.8], [5, 1.2]],
  [[0, 2.6]],
  [[3, 1.0], [6, 0.7]],
  [[0, 0.6], [3, 0.6], [6, 0.9]],
];

const mtof = (m: number): number => 440 * 2 ** ((m - 69) / 12);
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)] as T;
/** Bass root in A1 (33) .. G#2 (44). */
const bassRoot = (pc: number): number => 33 + ((pc - 9 + 12) % 12);

function stepDur(step: number): number {
  return step % 2 === 0 ? BEAT * SWING : BEAT * (1 - SWING);
}

// ── Engine ───────────────────────────────────────────────────────────────────────
interface Nodes {
  bass: GainNode;
  comp: GainNode;
  drums: GainNode;
  piano: GainNode;
  drone: GainNode;
  vibes: GainNode;
  organ: GainNode;
  choir: GainNode;
  bonus: GainNode;
  wowDepth: GainNode;
  flutterDepth: GainNode;
  tape: DelayNode;
  satDry: GainNode;
  satWet: GainNode;
  lowpass: BiquadFilterNode;
  revDry: GainNode;
  revWet: GainNode;
  duck: GainNode;
  out: GainNode;
}

function makeImpulse(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const t = i / ctx.sampleRate;
      // One-pole lowpassed noise that darkens over time, exponential decay.
      const k = 0.6 - 0.45 * (i / len);
      lp += k * (Math.random() * 2 - 1 - lp);
      d[i] = lp * Math.exp(-t * 2.6) * (i < 64 ? i / 64 : 1);
    }
  }
  return buf;
}

function makeCrushCurve(): Float32Array<ArrayBuffer> {
  const n = 2048;
  const levels = 24;
  const curve = new Float32Array(n);
  const norm = Math.tanh(2.5);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const y = Math.tanh(2.5 * x) / norm;
    curve[i] = Math.round(y * levels) / levels;
  }
  return curve;
}

/** Smoothly move a param to `value` from wherever it is now. */
function glide(ctx: BaseAudioContext, p: AudioParam, value: number, tau: number, at?: number): void {
  const now = at ?? ctx.currentTime;
  p.cancelScheduledValues(now);
  p.setValueAtTime(p.value, now);
  p.setTargetAtTime(value, now, tau);
}

export function createMusic(): MusicEngine {
  let ctx: AudioContext | null = null;
  let n: Nodes | null = null;
  let decay = 0;
  let wantPlaying = false;
  let playing = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let step = 0;
  let nextTime = 0;
  let gen = 0; // invalidates pending timeouts from earlier stop/tapeStop calls
  let droneSrcs: OscillatorNode[] = [];
  let compPattern: readonly (readonly [number, number])[] = COMP_PATTERNS[0] ?? [];
  let bassLine: number[] = [];
  let plannedBar = -1;
  let act = 0;
  let mode: MusicMode | null = null;

  function applyDecay(tau: number): void {
    if (!ctx || !n) return;
    const d = decay;
    glide(ctx, n.wowDepth.gain, WOW_MAX * d, tau);
    glide(ctx, n.flutterDepth.gain, FLUTTER_MAX * d, tau);
    const sat = 0.3 * d ** 1.5;
    glide(ctx, n.satWet.gain, sat, tau);
    glide(ctx, n.satDry.gain, 1 - sat * 0.5, tau);
    glide(ctx, n.lowpass.frequency, 18000 * (2500 / 18000) ** d, tau);
    const wet = 0.1 + 0.35 * d;
    glide(ctx, n.revWet.gain, wet, tau);
    glide(ctx, n.revDry.gain, 1 - wet * 0.5, tau);
    glide(ctx, n.piano.gain, clamp01((d - 0.6) / 0.3), tau);
    glide(ctx, n.drums.gain, clamp01((0.8 - d) / 0.1), tau);
    glide(ctx, n.drone.gain, clamp01((d - 0.7) / 0.3), tau);
    applyAct(tau);
  }

  /** Per-act layers fade in; a bonus loop silences the band (and the act layers) while it plays. */
  function applyAct(tau: number): void {
    if (!ctx || !n) return;
    const band = mode ? 0 : 1;
    glide(ctx, n.vibes.gain, act >= 1 ? band : 0, tau);
    glide(ctx, n.organ.gain, act >= 2 ? band : 0, tau);
    glide(ctx, n.choir.gain, act >= 3 ? band : 0, tau);
    glide(ctx, n.bass.gain, band, 0.08);
    glide(ctx, n.comp.gain, 0.82 * band, 0.08);
    glide(ctx, n.drums.gain, band * clamp01((0.8 - decay) / 0.1), 0.08);
    glide(ctx, n.bonus.gain, mode ? 1 : 0, 0.08);
  }

  function build(c: AudioContext, dest: AudioNode): Nodes {
    const g = (v: number): GainNode => {
      const x = c.createGain();
      x.gain.value = v;
      return x;
    };
    const preMix = g(1);
    const bass = g(1);
    const comp = g(0.82);
    const drums = g(1);
    const piano = g(0);
    const drone = g(0);
    const vibes = g(0);
    const organ = g(0);
    const choir = g(0);
    const bonus = g(1);
    for (const x of [bass, comp, drums, piano, drone, vibes, organ, choir, bonus]) x.connect(preMix);
    // The vibraphone's motor: a slow wide tremolo.
    const vibMotor = c.createOscillator();
    vibMotor.frequency.value = 5.6;
    const vibDepth = g(0.012);
    vibMotor.connect(vibDepth).connect(vibes.gain);
    vibMotor.start();

    // Rhodes tremolo on the comp bus.
    const trem = c.createOscillator();
    trem.frequency.value = 4.8;
    const tremDepth = g(0.16);
    trem.connect(tremDepth).connect(comp.gain);
    trem.start();

    // Tape: modulated delay line (the only path → pure pitch wobble, no chorus).
    const tape = c.createDelay(2.5);
    tape.delayTime.value = TAPE_D0;
    const wow = c.createOscillator();
    wow.frequency.value = WOW_HZ;
    const flutter = c.createOscillator();
    flutter.frequency.value = FLUTTER_HZ;
    const wowDepth = g(0);
    const flutterDepth = g(0);
    wow.connect(wowDepth).connect(tape.delayTime);
    flutter.connect(flutterDepth).connect(tape.delayTime);
    wow.start();
    flutter.start();
    preMix.connect(tape);

    // Saturation / crush (parallel).
    const satDry = g(1);
    const satWet = g(0);
    const shaper = c.createWaveShaper();
    shaper.curve = makeCrushCurve();
    shaper.oversample = '2x';
    const lowpass = c.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 18000;
    lowpass.Q.value = 0.5;
    tape.connect(satDry).connect(lowpass);
    tape.connect(shaper).connect(satWet).connect(lowpass);

    // Reverb (parallel).
    const conv = c.createConvolver();
    conv.buffer = makeImpulse(c, 2.8);
    const revDry = g(0.95);
    const revWet = g(0.1);
    const duck = g(1);
    lowpass.connect(revDry).connect(duck);
    lowpass.connect(conv).connect(revWet).connect(duck);

    const out = g(0);
    duck.connect(out).connect(dest);
    return { bass, comp, drums, piano, drone, vibes, organ, choir, bonus, wowDepth, flutterDepth, tape, satDry, satWet, lowpass, revDry, revWet, duck, out };
  }

  // ── Voices ─────────────────────────────────────────────────────────────────────
  /** Envelope helper: returns a gain node wired to `dest` that frees itself after `end`. */
  function env(c: AudioContext, dest: AudioNode, t: number, peak: number, attack: number, decayTau: number, sustain: number, end: number, release: number): GainNode {
    const gn = c.createGain();
    gn.gain.setValueAtTime(0, t);
    gn.gain.linearRampToValueAtTime(peak, t + attack);
    gn.gain.setTargetAtTime(peak * sustain, t + attack, decayTau);
    gn.gain.setTargetAtTime(0, end, release / 5);
    gn.connect(dest);
    return gn;
  }

  function oscAt(c: AudioContext, type: OscillatorType, freq: number, detune: number, t: number, stopAt: number, dest: AudioNode, onEnd?: () => void): OscillatorNode {
    const o = c.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.detune.value = detune;
    o.connect(dest);
    o.start(t);
    o.stop(stopAt);
    if (onEnd) o.onended = onEnd;
    return o;
  }

  function playBass(c: AudioContext, nd: Nodes, midi: number, t: number, dur: number): void {
    const f = mtof(midi);
    const lpF = c.createBiquadFilter();
    lpF.type = 'lowpass';
    lpF.Q.value = 0.7;
    lpF.frequency.setValueAtTime(1100, t);
    lpF.frequency.setTargetAtTime(380, t, 0.08);
    const gn = env(c, nd.bass, t, LEVEL.bass, 0.008, 0.12, 0.45, t + dur, 0.08);
    lpF.connect(gn);
    const stopAt = t + dur + 0.12;
    const o1 = oscAt(c, 'triangle', f, 0, t, stopAt, lpF, () => {
      lpF.disconnect();
      gn.disconnect();
    });
    const o2 = oscAt(c, 'sine', f, 0, t, stopAt, lpF);
    // Upright "thump": a hint of pitch settling at the attack.
    for (const o of [o1, o2]) {
      o.detune.setValueAtTime(25, t);
      o.detune.setTargetAtTime(0, t, 0.015);
    }
  }

  function playRhodes(c: AudioContext, nd: Nodes, midi: number, t: number, dur: number): void {
    const f = mtof(midi);
    const gn = env(c, nd.comp, t, LEVEL.comp, 0.025, 0.35, 0.6, t + dur, 0.3);
    const stopAt = t + dur + 0.35;
    oscAt(c, 'sine', f, 0, t, stopAt, gn, () => gn.disconnect());
    const warm = c.createGain();
    warm.gain.value = 0.35;
    warm.connect(gn);
    oscAt(c, 'triangle', f, 5, t, stopAt, warm, () => warm.disconnect());
    // Tine: short bright partial.
    const tine = c.createGain();
    tine.gain.setValueAtTime(0.18, t);
    tine.gain.setTargetAtTime(0, t, 0.05);
    tine.connect(gn);
    oscAt(c, 'sine', f * 4, 0, t, t + 0.4, tine, () => tine.disconnect());
  }

  function playPiano(c: AudioContext, nd: Nodes, midi: number, t: number): void {
    const f = mtof(midi);
    const off = -25 + Math.random() * 15; // out of tune, and not consistently
    const gn = env(c, nd.piano, t, LEVEL.piano, 0.004, 0.5, 0.25, t + 1.4, 0.5);
    const stopAt = t + 2;
    oscAt(c, 'triangle', f, off - 14, t, stopAt, gn, () => gn.disconnect());
    oscAt(c, 'triangle', f, off + 16, t, stopAt, gn);
    const hi = c.createGain();
    hi.gain.value = 0.3;
    hi.connect(gn);
    oscAt(c, 'sine', f * 2, off, t, stopAt, hi, () => hi.disconnect());
  }

  /** Vibraphone, slightly sour (+14 cents) in the decaying arcade. */
  function playVibes(c: AudioContext, nd: Nodes, midi: number, t: number): void {
    const f = mtof(midi);
    const gn = env(c, nd.vibes, t, LEVEL.vibes, 0.003, 0.6, 0.2, t + 1.6, 0.6);
    const stopAt = t + 2.2;
    oscAt(c, 'sine', f, 14, t, stopAt, gn, () => gn.disconnect());
    const hi = c.createGain();
    hi.gain.value = 0.25;
    hi.connect(gn);
    oscAt(c, 'sine', f * 4, 14, t, t + 0.5, hi, () => hi.disconnect());
  }

  /** Organ pad: drawbar-ish square + octave sine, a whole bar long. */
  function playOrgan(c: AudioContext, dest: AudioNode, midi: number, t: number, dur: number, level: number): void {
    const f = mtof(midi);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400;
    const gn = env(c, dest, t, level, 0.12, 1, 1, t + dur, 0.25);
    lp.connect(gn);
    const stopAt = t + dur + 0.3;
    oscAt(c, 'square', f, -4, t, stopAt, lp, () => {
      lp.disconnect();
      gn.disconnect();
    });
    oscAt(c, 'sine', f * 2, 3, t, stopAt, lp);
  }

  /** Choir "aah": a saw through two vowel formants. */
  function playChoir(c: AudioContext, nd: Nodes, midi: number, t: number, dur: number): void {
    const f = mtof(midi);
    const gn = env(c, nd.choir, t, LEVEL.choir, 0.35, 1, 1, t + dur, 0.5);
    const stopAt = t + dur + 0.6;
    const formants: BiquadFilterNode[] = [];
    for (const [fq, q] of [
      [730, 6],
      [1090, 8],
    ] as const) {
      const b = c.createBiquadFilter();
      b.type = 'bandpass';
      b.frequency.value = fq;
      b.Q.value = q;
      b.connect(gn);
      formants.push(b);
    }
    for (const det of [-9, 7]) {
      const o = c.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = det;
      for (const b of formants) o.connect(b);
      o.start(t);
      o.stop(stopAt);
      o.onended = () => {
        for (const b of formants) b.disconnect();
        gn.disconnect();
      };
    }
  }

  /** Bell / glockenspiel for the pachinko jingle. */
  function playBell(c: AudioContext, nd: Nodes, midi: number, t: number): void {
    const f = mtof(midi);
    const gn = env(c, nd.bonus, t, LEVEL.bell, 0.002, 0.18, 0, t + 0.5, 0.2);
    oscAt(c, 'sine', f, 0, t, t + 0.7, gn, () => gn.disconnect());
    const partial = c.createGain();
    partial.gain.value = 0.3;
    partial.connect(gn);
    oscAt(c, 'sine', f * 2.76, 0, t, t + 0.4, partial, () => partial.disconnect());
  }

  /** Big-band brass stab: detuned saws through a bright filter that closes. */
  function playBrass(c: AudioContext, nd: Nodes, midi: number, t: number, dur: number): void {
    const f = mtof(midi);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3200, t);
    lp.frequency.setTargetAtTime(900, t + 0.05, 0.12);
    const gn = env(c, nd.bonus, t, LEVEL.brass, 0.02, 0.2, 0.5, t + dur, 0.1);
    lp.connect(gn);
    const stopAt = t + dur + 0.15;
    oscAt(c, 'sawtooth', f, -6, t, stopAt, lp, () => {
      lp.disconnect();
      gn.disconnect();
    });
    oscAt(c, 'sawtooth', f, 6, t, stopAt, lp);
  }

  function noiseHit(c: AudioContext, dest: AudioNode, t: number, peak: number, attack: number, decayTau: number, len: number, filt: [BiquadFilterType, number, number][]): void {
    const src = c.createBufferSource();
    src.buffer = noiseBuffer(c);
    let node: AudioNode = src;
    const filters: BiquadFilterNode[] = [];
    for (const [type, freq, q] of filt) {
      const b = c.createBiquadFilter();
      b.type = type;
      b.frequency.value = freq;
      b.Q.value = q;
      node.connect(b);
      node = b;
      filters.push(b);
    }
    const gn = c.createGain();
    gn.gain.setValueAtTime(0, t);
    gn.gain.linearRampToValueAtTime(peak, t + attack);
    gn.gain.setTargetAtTime(0, t + attack, decayTau);
    node.connect(gn).connect(dest);
    src.start(t, Math.random() * 1.5, len);
    src.onended = () => {
      gn.disconnect();
      for (const b of filters) b.disconnect();
    };
  }

  function playKick(c: AudioContext, nd: Nodes, t: number): void {
    const gn = env(c, nd.drums, t, LEVEL.kick, 0.004, 0.06, 0, t + 0.15, 0.05);
    const o = oscAt(c, 'sine', 62, 0, t, t + 0.25, gn, () => gn.disconnect());
    o.frequency.setValueAtTime(80, t);
    o.frequency.setTargetAtTime(52, t, 0.03);
  }

  // ── Sequencer ──────────────────────────────────────────────────────────────────
  function planBar(bar: number): void {
    const [pc, q] = PROG[bar % BARS] ?? PROG[0]!;
    const [npc] = PROG[(bar + 1) % BARS] ?? PROG[0]!;
    const root = bassRoot(pc);
    const nextRoot = bassRoot(npc);
    const tones = QUAL[q].bass;
    const t2 = root + pick(tones.slice(1));
    let t3 = root + pick(tones);
    if (t3 === t2) t3 = root + 12 <= 50 ? root + 12 : root + 7;
    // Beat 4: chromatic approach from above/below, or the next root's fifth (dominant approach).
    const approach = pick([nextRoot - 1, nextRoot + 1, nextRoot + 7 > 50 ? nextRoot - 5 : nextRoot + 7]);
    bassLine = [root, t2, t3, approach];
    compPattern = pick(COMP_PATTERNS);
  }

  function voicing(pc: number, q: Quality): number[] {
    const v = QUAL[q].voicing;
    let base = 48 + pc;
    while (base + (v[0] ?? 0) < 51) base += 12;
    while (base + (v[0] ?? 0) > 62) base -= 12;
    return v.map((i) => base + i);
  }

  /** The bonus games' short loops (docs/07 §4.1), over a 4-bar turnaround. */
  function scheduleBonus(c: AudioContext, nd: Nodes, m: MusicMode, s: number, t: number): void {
    const bar = Math.floor(s / STEPS_PER_BAR);
    const inBar = s % STEPS_PER_BAR;
    const [pc, q] = BONUS_PROG[bar % BONUS_PROG.length]!;
    const root = bassRoot(pc);
    const v = voicing(pc, q);
    const tones = QUAL[q].bass.map((i) => 60 + pc + i);
    const bassTo = (note: number, len: number) => playBassTo(c, nd.bonus, note, t, len);
    switch (m) {
      case 'scratch': // ragtime stride: low root on 1 and 3, chord on 2 and 4, a skipping melody
        if (inBar === 0 || inBar === 4) bassTo(root - (inBar === 4 ? -7 : 0), BEAT * 0.8);
        if (inBar === 2 || inBar === 6) v.forEach((n2, i) => playRhodesTo(c, nd.bonus, n2 + 12, t + i * 0.004, BEAT * 0.5));
        if (inBar % 2 === 1 && Math.random() < 0.7) playBell(c, nd, pick(tones) + 12, t);
        break;
      case 'hilo': // tense tick-tock: a clock, a heartbeat bass, a held minor chord
        noiseHit(c, nd.bonus, t, LEVEL.snare * (inBar % 4 === 0 ? 0.9 : 0.6), 0.001, 0.02, 0.06, [['bandpass', inBar % 4 === 0 ? 2200 : 1500, 8]]);
        if (inBar === 0 || inBar === 1) bassTo(33 + 2, BEAT * 0.35);
        if (inBar === 0 && bar % 2 === 0) playOrgan(c, nd.bonus, 62, t, BEAT * 8, LEVEL.organ * 1.3);
        break;
      case 'wheel': // carnival waltz: oom on the downbeat, pah-pah after
        if (inBar === 0) bassTo(root, BEAT * 0.9);
        if (inBar === 3 || inBar === 5) v.forEach((n2) => playOrgan(c, nd.bonus, n2 + 12, t, BEAT * 0.6, LEVEL.organ * 1.4));
        if (inBar === 0 && Math.random() < 0.8) playBell(c, nd, pick(tones) + 24, t);
        break;
      case 'plinko': // pachinko jingle: bright arpeggios running up and down the chord
        playBell(c, nd, (tones[bar % 2 === 0 ? inBar % tones.length : (tones.length - 1 - (inBar % tones.length))] ?? 72) + 12, t);
        if (inBar === 0) bassTo(root, BEAT * 1.8);
        break;
      case 'feast': // big-band swing: walking bass, stabs on the and-of-2 and 4, a busy ride
        if (inBar % 2 === 0) bassTo(root + (QUAL[q].bass[inBar / 2] ?? 0), BEAT * 0.9);
        if (inBar === 3 || inBar === 6) v.forEach((n2) => playBrass(c, nd, n2 + 12, t, BEAT * (inBar === 3 ? 0.4 : 0.8)));
        if (inBar % 2 === 0 || inBar === 3 || inBar === 7) noiseHit(c, nd.bonus, t, LEVEL.ride * 1.2, 0.002, 0.07, 0.3, [['highpass', 6500, 0.7]]);
        if (inBar === 2 || inBar === 6) noiseHit(c, nd.bonus, t, LEVEL.snare, 0.01, 0.06, 0.25, [['bandpass', 2400, 0.8]]);
        break;
    }
  }

  function playBassTo(c: AudioContext, dest: GainNode, midi: number, t: number, dur: number): void {
    playBass(c, { ...(n as Nodes), bass: dest }, midi, t, dur);
  }

  function playRhodesTo(c: AudioContext, dest: GainNode, midi: number, t: number, dur: number): void {
    playRhodes(c, { ...(n as Nodes), comp: dest }, midi, t, dur);
  }

  function scheduleStep(c: AudioContext, nd: Nodes, s: number, t: number): void {
    if (mode) return scheduleBonus(c, nd, mode, s, t);
    const bar = Math.floor(s / STEPS_PER_BAR);
    const inBar = s % STEPS_PER_BAR;
    const [pc, q] = PROG[bar] ?? PROG[0]!;
    if (bar !== plannedBar) {
      planBar(bar);
      plannedBar = bar;
    }
    const human = (): number => (Math.random() - 0.5) * 0.012;

    // Bass: quarter notes.
    if (inBar % 2 === 0) {
      const note = bassLine[inBar / 2];
      if (note !== undefined) playBass(c, nd, note, t + human() * 0.5, BEAT * 0.92);
    }

    // Comp.
    for (const [cs, len] of compPattern) {
      if (cs !== inBar) continue;
      const notes = voicing(pc, q);
      notes.forEach((m, i) => playRhodes(c, nd, m, t + i * 0.006 + human(), BEAT * len));
    }

    // Drums (skip scheduling entirely once they've dropped out).
    if (decay < 0.8) {
      if (inBar === 2 || inBar === 6) {
        noiseHit(c, nd.drums, t, LEVEL.snare, 0.025, 0.07, 0.35, [['bandpass', 2800, 0.6], ['lowpass', 7000, 0.5]]);
      } else if (inBar % 2 === 1 && Math.random() < 0.35) {
        noiseHit(c, nd.drums, t, LEVEL.snare * 0.35, 0.03, 0.05, 0.2, [['bandpass', 3200, 0.6]]);
      }
      // Ride: ding (1) ding-a (2, and-of-2) ding (3) ding-a (4, and-of-4).
      if (inBar % 2 === 0 || inBar === 3 || inBar === 7) {
        const accent = inBar === 2 || inBar === 6 ? 1.2 : inBar % 2 === 1 ? 0.7 : 1;
        noiseHit(c, nd.drums, t + human() * 0.5, LEVEL.ride * accent, 0.002, inBar % 2 ? 0.05 : 0.09, 0.4, [['highpass', 6500, 0.7], ['peaking', 9000, 2]]);
      }
      if (inBar % 2 === 0) playKick(c, nd, t);
    }

    // Act II+: the sour vibraphone answers the comp on a chord tone.
    if (act >= 1 && (inBar === 0 || inBar === 5) && Math.random() < (inBar === 0 ? 0.6 : 0.35)) {
      const v = voicing(pc, q);
      playVibes(c, nd, pick(v) + 12, t + 0.01);
    }
    // Act III+: an organ pad holds the chord; Act IV: the choir sings its top.
    if (inBar === 0) {
      const v = voicing(pc, q);
      if (act >= 2) for (const m of v.slice(0, 3)) playOrgan(c, nd.organ, m, t, BEAT * 3.8, LEVEL.organ);
      if (act >= 3) for (const m of v.slice(1, 3)) playChoir(c, nd, m + 12, t + 0.05, BEAT * 3.9);
    }

    // Detuned piano (decay layer).
    if (decay > 0.55) {
      const v = voicing(pc, q);
      if (inBar === 0) {
        playPiano(c, nd, (v[3] ?? 72) + 12, t + 0.02);
        playPiano(c, nd, (v[1] ?? 67) + 12, t + 0.05);
      } else if (inBar === 5 && Math.random() < 0.5) {
        playPiano(c, nd, pick(v) + 12, t);
      }
    }
  }

  function tick(): void {
    const c = ctx;
    const nd = n;
    if (!c || !nd || !playing) return;
    try {
      const hidden = typeof document !== 'undefined' && document.hidden;
      const ahead = hidden ? LOOKAHEAD_HIDDEN : LOOKAHEAD;
      const now = c.currentTime;
      if (nextTime < now) {
        // Fell behind (throttled timer / resumed context): realign on the next step boundary.
        while (nextTime < now) {
          nextTime += stepDur(step);
          step = (step + 1) % LOOP_STEPS;
        }
      }
      let guard = 0;
      while (nextTime < now + ahead && guard++ < 64) {
        scheduleStep(c, nd, step, nextTime);
        nextTime += stepDur(step);
        step = (step + 1) % LOOP_STEPS;
      }
    } catch {
      /* never let the scheduler throw */
    }
  }

  function startDrone(c: AudioContext, nd: Nodes): void {
    stopDrone(c.currentTime);
    const lpF = c.createBiquadFilter();
    lpF.type = 'lowpass';
    lpF.frequency.value = 170;
    lpF.Q.value = 2.5;
    const sweep = c.createOscillator();
    sweep.frequency.value = 0.07;
    const sweepDepth = c.createGain();
    sweepDepth.gain.value = 60;
    sweep.connect(sweepDepth).connect(lpF.frequency);
    const lvl = c.createGain();
    lvl.gain.value = LEVEL.drone;
    lpF.connect(lvl).connect(nd.drone);
    const t = c.currentTime;
    const a = oscAt(c, 'sawtooth', mtof(26), 0, t, t + 1e6, lpF, () => {
      lpF.disconnect();
      lvl.disconnect();
      sweepDepth.disconnect();
    });
    const b = oscAt(c, 'sawtooth', mtof(38), 8, t, t + 1e6, lpF);
    const sub = oscAt(c, 'sine', mtof(26), 0, t, t + 1e6, lvl);
    sweep.start(t);
    droneSrcs = [a, b, sub, sweep];
  }

  function stopDrone(at: number): void {
    for (const o of droneSrcs) {
      try {
        o.stop(at);
      } catch {
        /* already stopped */
      }
    }
    droneSrcs = [];
  }

  function haltScheduler(): void {
    playing = false;
    if (timer !== null) clearInterval(timer);
    timer = null;
  }

  function begin(): void {
    const c = ctx;
    const nd = n;
    if (!c || !nd) return;
    gen++;
    haltScheduler();
    const now = c.currentTime;
    // Reset the tape (in case of a tape stop) while silent, then fade in.
    nd.tape.delayTime.cancelScheduledValues(now);
    nd.tape.delayTime.setValueAtTime(TAPE_D0, now);
    glide(c, nd.out.gain, 1, 0.25);
    step = 0;
    plannedBar = -1;
    nextTime = now + 0.1;
    playing = true;
    if (droneSrcs.length === 0) startDrone(c, nd);
    timer = setInterval(tick, TICK_MS);
    tick();
  }

  return {
    attach(c: AudioContext, dest: AudioNode): void {
      if (ctx) return;
      try {
        ctx = c;
        n = build(c, dest);
        applyDecay(0.01);
        if (wantPlaying) begin();
      } catch {
        ctx = null;
        n = null;
      }
    },
    start(): void {
      wantPlaying = true;
      if (playing) return;
      try {
        begin();
      } catch {
        /* ignore */
      }
    },
    stop(): void {
      wantPlaying = false;
      const c = ctx;
      const nd = n;
      haltScheduler();
      if (!c || !nd) return;
      try {
        glide(c, nd.out.gain, 0, 0.2);
        const my = ++gen;
        setTimeout(() => {
          if (my === gen && !playing) stopDrone(0);
        }, 1500);
      } catch {
        /* ignore */
      }
    },
    setDecay(d: number): void {
      decay = clamp01(Number.isFinite(d) ? d : 0);
      try {
        applyDecay(SMOOTH);
      } catch {
        /* ignore */
      }
    },
    duck(db: number, ms: number): void {
      const c = ctx;
      const nd = n;
      if (!c || !nd) return;
      try {
        const now = c.currentTime;
        const target = 10 ** (Math.min(0, db) / 20);
        glide(c, nd.duck.gain, target, 0.04);
        nd.duck.gain.setTargetAtTime(1, now + Math.max(0, ms) / 1000, 0.2);
      } catch {
        /* ignore */
      }
    },
    setAct(a: number): void {
      act = Math.max(0, Math.min(3, Math.round(a)));
      try {
        applyAct(SMOOTH);
      } catch {
        /* ignore */
      }
    },
    setMode(m: MusicMode | null): void {
      if (m === mode) return;
      mode = m;
      step = 0;
      plannedBar = -1;
      try {
        applyAct(0.05);
      } catch {
        /* ignore */
      }
    },
    tapeStop(): void {
      wantPlaying = false;
      const c = ctx;
      const nd = n;
      if (!c || !nd || !playing) return;
      haltScheduler();
      try {
        const now = c.currentTime;
        const T = TAPE_STOP_S;
        const N = 128;
        const curve = new Float32Array(N);
        for (let i = 0; i < N; i++) {
          const t = (i / (N - 1)) * T;
          curve[i] = TAPE_D0 + (t * t) / (2 * T);
        }
        nd.tape.delayTime.cancelScheduledValues(now);
        nd.tape.delayTime.setValueCurveAtTime(curve, now, T);
        nd.out.gain.cancelScheduledValues(now);
        nd.out.gain.setValueAtTime(nd.out.gain.value, now);
        nd.out.gain.setTargetAtTime(0, now + T * 0.6, T * 0.12);
        nd.out.gain.setValueAtTime(0, now + T + 0.05);
        const my = ++gen;
        setTimeout(() => {
          if (my !== gen || !ctx || !n) return;
          stopDrone(0);
          const t = ctx.currentTime;
          n.tape.delayTime.cancelScheduledValues(t);
          n.tape.delayTime.setValueAtTime(TAPE_D0, t);
        }, (T + 0.3) * 1000);
      } catch {
        /* ignore */
      }
    },
  };
}
